import { useEffect, useRef, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"

function resolveAuthoredValue(
  value: unknown,
  resolveSymbol: (value: string) => unknown,
): unknown {
  return typeof value === "string" ? resolveSymbol(value) : value
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  if (value === undefined || value === null) return fallback
  return value === true || value === "true"
}

function errorRecord(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, message]) => [
      key,
      String(message ?? ""),
    ]),
  )
}

function invalidMessage(
  control: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
): string {
  if (!control.validity.valueMissing) {
    return control.validationMessage || "Invalid value"
  }
  const rawLabel =
    control.getAttribute("aria-label") ||
    ("placeholder" in control ? control.placeholder : "") ||
    control.name ||
    control.id ||
    "Field"
  const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1)
  return `${label} is required`
}

export function FormRenderer({ element }: RendererProps) {
  const { dispatchEvent, resolveSymbol } = useRenderContext()
  const atomDefaults = useAtomDefaults("form")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const elementRecord = element as unknown as Record<string, unknown>
  const authoredErrors = errorRecord(
    resolveAuthoredValue(elementRecord.errors, resolveSymbol),
  )
  const authoredErrorsSignature = JSON.stringify(authoredErrors)
  const authoredIsSubmitting = booleanValue(
    resolveAuthoredValue(elementRecord.isSubmitting, resolveSymbol),
    false,
  )
  const authoredIsValid = booleanValue(
    resolveAuthoredValue(elementRecord.isValid, resolveSymbol),
    true,
  )
  const [errors, setErrors] = useState(authoredErrors)
  const [isSubmitting, setIsSubmitting] = useState(authoredIsSubmitting)
  const [isValid, setIsValid] = useState(authoredIsValid)
  const errorsRef = useRef(errors)

  useEffect(() => {
    errorsRef.current = authoredErrors
    setErrors(authoredErrors)
  }, [element, authoredErrorsSignature])

  useEffect(() => {
    setIsSubmitting(authoredIsSubmitting)
  }, [element, authoredIsSubmitting])

  useEffect(() => {
    setIsValid(authoredIsValid)
  }, [element, authoredIsValid])

  const beginSubmit = () => {
    setIsSubmitting(true)
    dispatchEvent(element.id, "submittingChange", {
      isSubmitting: true,
      submitting: true,
    })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    beginSubmit()
    const formData = new FormData(e.currentTarget)
    const data: Record<string, string> = {}
    formData.forEach((value, key) => {
      data[key] = String(value)
    })
    errorsRef.current = {}
    setErrors({})
    setIsValid(true)
    dispatchEvent(element.id, "submit", {
      data,
      errors: {},
      isSubmitting: true,
      submitting: true,
      isValid: true,
      valid: true,
    })
  }

  const handleInvalid = (event: React.FormEvent<HTMLFormElement>) => {
    const control = event.target
    if (
      !(control instanceof HTMLInputElement) &&
      !(control instanceof HTMLTextAreaElement) &&
      !(control instanceof HTMLSelectElement)
    ) return
    const field = control.name || control.id || "field"
    const nextErrors = {
      ...errorsRef.current,
      [field]: invalidMessage(control),
    }
    errorsRef.current = nextErrors
    setErrors(nextErrors)
    setIsValid(false)
    dispatchEvent(element.id, "validationChange", {
      errors: nextErrors,
      isValid: false,
      valid: false,
    })
  }

  const handleClickCapture = (event: React.MouseEvent<HTMLFormElement>) => {
    const target = event.target
    if (!(target instanceof Element)) return
    const submitter = target.closest(
      'button[type="submit"], input[type="submit"], input[type="image"]',
    )
    if (submitter && event.currentTarget.contains(submitter)) beginSubmit()
  }

  return (
    <form
      onSubmit={handleSubmit}
      onInvalidCapture={handleInvalid}
      onClickCapture={handleClickCapture}
      className={element.className}
      data-testid={`form-${element.id}`}
      data-atom-type="form"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-error-count={Object.keys(errors).length}
      data-submitting={isSubmitting ? "true" : "false"}
      data-valid={isValid ? "true" : "false"}
      aria-busy={isSubmitting}
      aria-invalid={!isValid}
    >
      {element.children?.map((child) => (
        <ElementRenderer key={child.id} element={child} />
      ))}
      {Object.entries(errors).map(([field, message]) => (
        <p
          key={field}
          role="alert"
          data-form-error-for={field}
        >
          {message}
        </p>
      ))}
    </form>
  )
}
